
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import api from '../../api/api';
import Button from '../../components/ui/Button';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { FileEdit } from "lucide-react";

const ItemListPage = ({ setView }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [editRowId, setEditRowId] = useState(null);
  const [editForm, setEditForm] = useState({ standardcost: '', mrp: '' });
  const [viewMode, setViewMode] = useState('card'); // 'table' or 'card'

  useEffect(() => {
    api.getItemList(user.UsrName)
      .then(data => {
        const list = data.Table || [];
        setItems(list);
        setFilteredItems(list);
      })
      .catch(() => setError('Failed to fetch items.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  useEffect(() => {
    const filtered = items.filter(item =>
      item.item_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Item_desc1?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredItems(filtered);
  }, [searchTerm, items]);

  const startEdit = (item) => {
    setEditRowId(item.itemid);
    setEditForm({ standardcost: item.standardcost, mrp: item.mrp });
  };

  const cancelEdit = () => {
    setEditRowId(null);
    setEditForm({ standardcost: '', mrp: '' });
  };

  const saveEdit = async (itemid) => {
    try {
      await api.ItemRateUpdate({
        ItemId: itemid,
        StandardCost: parseFloat(editForm.standardcost),
        Mrp: parseFloat(editForm.mrp),
        User: user.UsrName
      });
      setItems(prev =>
        prev.map(it =>
          it.itemid === itemid
            ? { ...it, standardcost: parseFloat(editForm.standardcost), mrp: parseFloat(editForm.mrp) }
            : it
        )
      );
      cancelEdit();
    } catch {
      alert('Failed to update item.');
    }
  };

  return (
    <div className="w-full min-h-screen p-0 m-0 bg-gray-100">
      {/* Header + View Toggle */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 px-3 py-3 bg-grey border-b">
        <Heading>Price List</Heading>
        <div className="flex gap-1">
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded ${viewMode === 'card' ? 'bg-blue-500 text-white' : 'bg-white-200 text-gray-600'}`}
          >
            {/* Grid/Card Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-white-200 text-gray-600'}`}
          >
            {/* Table/List Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Back + Search */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray ">
        <Button
          onClick={() => setView("home")}
          className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
        >
          ⬅
        </Button>
        <SearchInput
          type="text"
          placeholder="Search by Item No or Description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="p-3">
        {loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredItems.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No items found.</div>
        ) : viewMode === 'table' ? (
          // Table View
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-sm rounded-lg font-medium text-left text-gray-900 uppercase">Item No</th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">Description</th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">Standard Cost</th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">MRP</th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">Stock</th>
                  <th className="px-4 py-2 text-sm font-medium text-center text-gray-900 uppercase">Image</th>
                  <th className="px-4 py-2 text-sm font-medium text-center text-gray-900 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map(item => (
                  <tr key={item.itemid}>
                    <td className="px-4 py-2">{item.item_no}</td>
                    <td className="px-4 py-2">{item.Item_desc1}</td>
                    <td className="px-4 py-2 text-right">
                      {editRowId === item.itemid ? (
                        <input
                          type="number"
                          value={editForm.standardcost}
                          onChange={(e) => setEditForm({ ...editForm, standardcost: e.target.value })}
                          className="w-20 px-2 py-1 border rounded"
                        />
                      ) : (
                        <>₹{item.standardcost.toLocaleString('en-IN')}</>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {editRowId === item.itemid ? (
                        <input
                          type="number"
                          value={editForm.mrp}
                          onChange={(e) => setEditForm({ ...editForm, mrp: e.target.value })}
                          className="w-20 px-2 py-1 border rounded"
                        />
                      ) : (
                        <>₹{item.mrp.toLocaleString('en-IN')}</>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">{item.StkQty}</td>
                    <td className="px-4 py-2 text-center">
                      {item.Filepath && (
                        <img
                          src={item.Filepath.startsWith("http") ? item.Filepath : `/uploads/${item.Filepath}`}
                          alt="Item"
                          className="object-cover w-12 h-12 rounded"
                        />
                      )}
                    </td>

                    <td className="px-4 py-2 text-center">
                      {editRowId === item.itemid ? (
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            onClick={() => saveEdit(item.itemid)}
                            className="px-3 py-1 text-xs text-white bg-blue-500 rounded hover:bg-blue-600"
                          >
                            Update
                          </Button>
                          <Button
                            onClick={cancelEdit}
                            className="px-3 py-1 text-xs text-white bg-gray-500 rounded hover:bg-gray-600"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => startEdit(item)}
                          className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                        >
                          <FileEdit className="w-5 h-5" />
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredItems.map(item => (
              <div key={item.itemid} className="p-3 bg-white border rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">{item.item_no}</div>
                    <div className="text-xs text-gray-600">{item.Item_desc1}</div>
                  </div>
                  {item.Filepath && (
                    <img
                      src={item.Filepath.startsWith("http") ? item.Filepath : `/uploads/${item.Filepath}`}
                      alt="Item"
                      className="object-cover w-12 h-12 rounded"
                    />
                  )}

                </div>
                {editRowId === item.itemid ? (
                  <>
                    <div className="text-xs text-gray-700">
                      <strong>Cost:</strong>
                      <input
                        type="number"
                        value={editForm.standardcost}
                        onChange={(e) => setEditForm({ ...editForm, standardcost: e.target.value })}
                        className="w-full px-2 py-1 mt-1 border rounded"
                      />
                    </div>
                    <div className="mt-2 text-xs text-gray-700">
                      <strong>MRP:</strong>
                      <input
                        type="number"
                        value={editForm.mrp}
                        onChange={(e) => setEditForm({ ...editForm, mrp: e.target.value })}
                        className="w-full px-2 py-1 mt-1 border rounded"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => saveEdit(item.itemid)} className="px-2 py-1 text-xs text-white bg-blue-500 rounded hover:bg-blue-600">Update</Button>
                      <Button onClick={cancelEdit} className="px-2 py-1 text-xs text-white bg-blue-500 rounded hover:bg-blue-600">Cancel</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-gray-700"><strong>Cost:</strong> ₹{item.standardcost.toLocaleString('en-IN')}</div>
                    <div className="text-xs text-gray-700"><strong>MRP:</strong> ₹{item.mrp.toLocaleString('en-IN')}</div>
                    <div className="text-xs text-gray-700"><strong>Stock:</strong> {item.StkQty}</div>
                    <Button onClick={() => startEdit(item)} className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"> <FileEdit className="w-5 h-5" />

                      Edit</Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemListPage;


